'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { homeAssetSrc } from '../homeAssetSrc';
import {
  addOutgoingRing,
  createWaveSim,
  renderWave,
  stepWave,
} from './weightWaveSim';
import styles from './WeightRipples.module.css';

const CSS_RINGS = [
  {
    src: homeAssetSrc('/home/form-factors/ellipse-air-outer.svg'),
    widthPct: 103.09,
    delay: '0s',
  },
  {
    src: homeAssetSrc('/home/form-factors/ellipse-air-mid.svg'),
    widthPct: 77.809,
    delay: '1.1s',
  },
  {
    src: homeAssetSrc('/home/form-factors/ellipse-air-inner.svg'),
    widthPct: 53.933,
    delay: '2.2s',
  },
] as const;

const LOCKED = {
  speed: 0.15,
  frequency: 0.75,
  gridMax: 96,
  dprCap: 1,
  ambientPeriod: 2.4,
  centerAmp: 0.85,
  centerRadius: 5,
  c2: 0.1,
  damp: 0.985,
  edge: 8,
  base: 236,
  heightGain: 58,
  lightGain: 72,
  clampMin: 230,
  clampMax: 238,
  ringSigma: 2.1,
  ringOutward: 1.05,
  jitterMin: -0.55,
  jitterMax: 0.7,
} as const;

const SPAWN_PERIOD = LOCKED.ambientPeriod / Math.max(0.05, LOCKED.frequency);

function nextSpawnWait(): number {
  const jitter =
    LOCKED.jitterMin + Math.random() * (LOCKED.jitterMax - LOCKED.jitterMin);
  return Math.max(0.4, SPAWN_PERIOD + jitter);
}

function dropCenterRing(sim: Parameters<typeof addOutgoingRing>[0]): void {
  addOutgoingRing(
    sim,
    0.5,
    0.5,
    LOCKED.centerRadius,
    LOCKED.ringSigma,
    LOCKED.centerAmp,
    LOCKED.ringOutward,
  );
}

type Mode = 'canvas' | 'css' | 'static';

type NetworkInformationLike = { saveData?: boolean };

function getConnection(): NetworkInformationLike | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

/** The wave sim is a per-frame CPU cost — skip it on low-end / data-saver. */
function canUseCanvasSim(): boolean {
  if (getConnection()?.saveData) return false;
  if ((navigator.hardwareConcurrency || 8) <= 4) return false;
  const memory = (navigator as Navigator & { deviceMemory?: number })
    .deviceMemory;
  return (memory || 8) > 4;
}

function resolveMode(): Mode {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 'static';
  }
  return canUseCanvasSim() ? 'canvas' : 'css';
}

export const WeightRipples = () => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);

  useEffect(() => {
    setMode(resolveMode());
  }, []);

  useEffect(() => {
    if (mode !== 'canvas') return;
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    let raf = 0;
    let running = true;
    let visible = true;
    let lastTs = 0;
    let spawnWait = nextSpawnWait();
    const pad = Math.max(24, LOCKED.edge);
    let sim = createWaveSim(96, 70, LOCKED.edge, pad);
    let pixels = new Uint8ClampedArray(96 * 70 * 4);
    let image = new ImageData(pixels, 96, 70);
    const offscreen = document.createElement('canvas');
    offscreen.width = 96;
    offscreen.height = 70;
    const offCtx = offscreen.getContext('2d', { alpha: false });
    if (!offCtx) return;

    const rebuild = (cols: number, rows: number) => {
      sim = createWaveSim(cols, rows, LOCKED.edge, pad);
      pixels = new Uint8ClampedArray(cols * rows * 4);
      image = new ImageData(pixels, cols, rows);
      offscreen.width = cols;
      offscreen.height = rows;
      dropCenterRing(sim);
      spawnWait = nextSpawnWait();
    };

    const resize = () => {
      const rect = root.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      const dpr = Math.min(LOCKED.dprCap, window.devicePixelRatio || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.fillStyle = `rgb(${LOCKED.base}, ${LOCKED.base}, ${LOCKED.base})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const long = Math.max(w, h);
      const cols = Math.max(48, Math.round((LOCKED.gridMax * w) / long));
      const rows = Math.max(36, Math.round((LOCKED.gridMax * h) / long));
      if (cols !== sim.visWidth || rows !== sim.visHeight) rebuild(cols, rows);
    };

    const tick = (ts: number) => {
      if (!running) return;
      raf = 0;
      if (!visible) {
        lastTs = 0;
        return;
      }

      const dt = lastTs ? Math.min(0.05, (ts - lastTs) / 1000) : 0.016;
      lastTs = ts;
      spawnWait -= dt;

      if (spawnWait <= 0) {
        dropCenterRing(sim);
        spawnWait = nextSpawnWait();
      }

      stepWave(sim, { c2: LOCKED.c2 * LOCKED.speed, damp: LOCKED.damp });
      renderWave(sim, pixels, {
        base: LOCKED.base,
        heightGain: LOCKED.heightGain,
        lightGain: LOCKED.lightGain,
        clampMin: LOCKED.clampMin,
        clampMax: LOCKED.clampMax,
      });
      offCtx.putImageData(image, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(offscreen, 0, 0, canvas.width, canvas.height);

      raf = requestAnimationFrame(tick);
    };

    const ensureTick = () => {
      if (!running || !visible || raf) return;
      lastTs = 0;
      raf = requestAnimationFrame(tick);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(root);

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = Boolean(entry?.isIntersecting);
        if (visible) ensureTick();
      },
      { threshold: 0.05 },
    );
    io.observe(root);

    ensureTick();

    return () => {
      running = false;
      ro.disconnect();
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [mode]);

  return (
    <div className={styles.wrap}>
      <div ref={rootRef} className={styles.root} aria-hidden="true">
        {mode === 'canvas' ? (
          <canvas ref={canvasRef} className={styles.canvas} />
        ) : mode ? (
          CSS_RINGS.map((ring) => (
            <img
              key={ring.src}
              src={ring.src}
              alt=""
              className={`${styles.cssRing} ${
                mode === 'css' ? styles.cssRingAnimated : ''
              }`}
              style={
                {
                  '--ripple-w': `${ring.widthPct}%`,
                  '--ripple-delay': ring.delay,
                } as CSSProperties
              }
            />
          ))
        ) : null}
      </div>
    </div>
  );
};
